import _ from 'lodash'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { useGetConsultationQuery } from '@/core/services/consultation'
import { useGetExpertiseQuery, useUpdateExpertiseMutation } from '@/core/services/expertise'

import { getApiErrorMessage } from '@/utils/errors'

import ExpertiseConsultationDialog, { ExpertiseConsultationDialogProps } from '../components/ExpertiseConsultationDialog'

type ExpertiseConsultationDialogContainerProps = Pick<ExpertiseConsultationDialogProps, 'onClose' | 'open' | 'readOnly'> & {
  consultationId?: string
}

export default function ExpertiseConsultationDialogContainer(props: ExpertiseConsultationDialogContainerProps) {
  const { consultationId, ...rest } = props
  const { expertiseId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: consultation } = useGetConsultationQuery(consultationId ?? skipToken)
  const { data: expertise } = useGetExpertiseQuery(expertiseId ?? skipToken)
  const [updateExpertise] = useUpdateExpertiseMutation()

  const handleAdd = async (consultationId: string) => {
    if (!expertise) return

    try {
      await updateExpertise({ id: expertise._id, consultations: _.map(expertise.consultations, '_id').concat(consultationId) }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.update.error')), { variant: 'error' })
    }
  }

  const handleDelete = async (consultationId: string) => {
    if (!expertise) return

    try {
      const consultations = _.map(
        _.filter(expertise.consultations, (consultation) => consultation._id !== consultationId),
        '_id'
      )
      await updateExpertise({ id: expertise._id, consultations }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.update.error')), { variant: 'error' })
    }
  }

  if (!consultation) return null

  return (
    <ExpertiseConsultationDialog
      {...rest}
      consultation={consultation}
      isAdded={!!_.find(expertise?.consultations, ({ _id }) => _id === consultationId)}
      onAdd={handleAdd}
      onDelete={handleDelete}
    />
  )
}
